import React from 'react';
import {
    useBranchApiTokenQuery,
    useGenerateBranchApiTokenMutation,
    useRevealBranchApiTokenMutation,
    useRevokeBranchApiTokenMutation,
} from '@/api/queries';
import type { Branch } from '../branchesMeta';

export type UseBranchEnrollResult = {
    isOpen: boolean;
    hasToken: boolean;
    setAt: string | null;
    /**
     * The plaintext token, present only in the moment after this admin
     * generated or rotated it. It is never read back from the server, so once
     * the modal closes it is gone for good.
     */
    revealedToken: string | null;
    /**
     * Last four characters of the active token, from
     * `GET /branches/{id}/api-token`. Enough to tell which token a terminal is
     * holding; null while it loads, for tokens issued before the server
     * recorded previews, and when there is no token.
     */
    tokenPreview: string | null;
    /**
     * The preview lookup is in flight after the modal opened for a branch with
     * a token. The view keeps the surrounding copy and shows a placeholder in
     * place of the preview rather than flashing a wrong state.
     */
    isLoadingToken: boolean;
    copied: boolean;
    error: string | null;
    isGenerating: boolean;
    isRevoking: boolean;
    /** The active token is being fetched to copy (BOS-209). */
    isCopying: boolean;
    onOpen: () => void;
    onClose: () => void;
    onGenerate: () => void;
    onRevoke: () => void;
    onCopy: () => void;
    /** Copies the already-active token, fetched from the server on demand. */
    onCopyActive: () => void;
};

/**
 * Drives a single branch's terminal-enrollment flow: generate, rotate, revoke.
 *
 * The token is shown on screen once, at the moment it is minted. bos-api used to
 * keep the plaintext so this modal could re-reveal it on open; that meant any
 * admin could read a live terminal credential at will and a database dump
 * leaked every one of them, so the server now keeps only a hash and the
 * token's last four characters (BOS-118). What this hook reads back is that
 * preview — enough to answer "which token is this terminal on?", useless to
 * anyone who intercepts it. BOS-209 adds `onCopyActive`: the server keeps an
 * encrypted copy and `POST …/api-token/reveal` returns it, so an admin can
 * copy a live token without rotating. The plaintext goes straight to the
 * clipboard and is never held in state or cache — unless the clipboard is
 * unavailable, in which case it is shown for a manual copy.
 *
 * The preview query is gated on `isOpen && hasToken` — no lookups for branches
 * nobody is looking at. `revealedToken` lives in local state only and is
 * cleared on close, because there is nowhere to recover it from.
 */
export const useBranchEnroll = (branch: Branch): UseBranchEnrollResult => {
    const generateMutation = useGenerateBranchApiTokenMutation();
    const revokeMutation = useRevokeBranchApiTokenMutation();
    const revealMutation = useRevealBranchApiTokenMutation();

    const [isOpen, setIsOpen] = React.useState(false);
    const [revealedToken, setRevealedToken] = React.useState<string | null>(
        null,
    );
    const [copied, setCopied] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);

    // Token-set state comes off the branch row rather than this query, so the
    // table renders it without a request per row; the generate/revoke
    // mutations patch `apiTokenSetAt` into the list cache to keep it in step.
    const hasToken = Boolean(branch.apiTokenSetAt);
    const apiTokenQuery = useBranchApiTokenQuery(branch.id, isOpen && hasToken);

    React.useEffect(() => {
        if (!isOpen || !apiTokenQuery.data || apiTokenQuery.data.success) {
            return;
        }
        setError(apiTokenQuery.data.error.message ?? 'Failed to read token.');
    }, [isOpen, apiTokenQuery.data]);

    const onOpen = React.useCallback(() => {
        setRevealedToken(null);
        setCopied(false);
        setError(null);
        setIsOpen(true);
    }, []);

    const onClose = React.useCallback(() => {
        setIsOpen(false);
        setRevealedToken(null);
        setCopied(false);
        setError(null);
    }, []);

    const onGenerate = React.useCallback(async () => {
        setError(null);
        setCopied(false);
        const result = await generateMutation.mutateAsync(branch.id);
        if (!result.success) {
            setError(result.error.message ?? 'Failed to generate token.');
            return;
        }
        setRevealedToken(result.response.token);
    }, [branch.id, generateMutation]);

    const onRevoke = React.useCallback(async () => {
        setError(null);
        const result = await revokeMutation.mutateAsync(branch.id);
        if (!result.success) {
            setError(result.error.message ?? 'Failed to revoke token.');
            return;
        }
        setRevealedToken(null);
        setIsOpen(false);
    }, [branch.id, revokeMutation]);

    const onCopy = React.useCallback(() => {
        if (!revealedToken) {
            return;
        }
        void navigator.clipboard?.writeText(revealedToken);
        setCopied(true);
    }, [revealedToken]);

    const onCopyActive = React.useCallback(async () => {
        setError(null);
        setCopied(false);
        const result = await revealMutation.mutateAsync(branch.id);
        if (!result.success) {
            setError(result.error.message ?? 'Failed to read token.');
            return;
        }
        const { token } = result.response;
        try {
            await navigator.clipboard.writeText(token);
            setCopied(true);
        } catch {
            // No clipboard (insecure context, permission denied): fall back to
            // showing the token so it can be copied by hand.
            setRevealedToken(token);
        }
    }, [branch.id, revealMutation]);

    const tokenPreview =
        (apiTokenQuery.data?.success === true
            ? apiTokenQuery.data.response.tokenPreview
            : null) ?? null;

    const isLoadingToken = isOpen && hasToken && apiTokenQuery.isPending;

    return {
        isOpen,
        hasToken,
        setAt: branch.apiTokenSetAt ?? null,
        revealedToken,
        tokenPreview,
        isLoadingToken,
        copied,
        error,
        isGenerating: generateMutation.isPending,
        isRevoking: revokeMutation.isPending,
        isCopying: revealMutation.isPending,
        onOpen,
        onClose,
        onGenerate,
        onRevoke,
        onCopy,
        onCopyActive,
    };
};
